/**
 * One event, with tabs.
 *
 * Sponsors first and Event Setup second, per the settled design. The order is
 * deliberate: staff open an event to see who has and has not sent a logo, not
 * to change its dates. Setup is where you go occasionally; sponsors is where
 * the work is.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import Sponsors from './Sponsors.tsx';
import EventSetup, { type EventData } from './EventSetup.tsx';
import { T } from '../lib/copyEdit.tsx';

type EventDetailData = EventData & {
  deadline: EventData['deadline'] & { display: string | null; daysUntil: number | null };
};

const STATUS_BADGE: Record<EventDetailData['status'], string> = {
  draft: 'b-neutral',
  active: 'b-info',
  complete: 'b-success',
  archived: 'b-neutral',
};

/**
 * The deadline in human terms.
 *
 * "Apr 10, 2026 (5:00 PM ET)" answers when, and does not answer how soon —
 * which is the thing anyone opening an event actually wants, and the thing the
 * prototype puts in red beside it. Shared wording with the dashboard, because
 * the same event described two ways on two screens reads as two events.
 */
function describeDays(days: number): string {
  if (days < 0) return `${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} past deadline`;
  if (days === 0) return 'due today';
  if (days === 1) return 'due tomorrow';
  return `${days} days away`;
}

/** Dates as a range, collapsing a single-day event to one date rather than
 *  printing it twice. */
function describeWhen(startsOn: string | null, endsOn: string | null): string | null {
  if (!startsOn) return null;
  const fmt = (iso: string) =>
    new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  if (!endsOn || endsOn === startsOn) return fmt(startsOn);
  return `${fmt(startsOn)} to ${fmt(endsOn)}`;
}

export default function EventDetail() {
  const { clientId = '', eventId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'setup' ? 'setup' : 'sponsors';

  const [event, setEvent] = useState<EventDetailData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`/api/clients/${clientId}/events/${eventId}`);
      if (!res.ok) throw new Error(String(res.status));
      const body = (await res.json()) as { event: EventDetailData };
      setEvent(body.event);
    } catch {
      setError('Could not load this event.');
    }
  }, [clientId, eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <p className="auth-error">{error}</p>;
  if (!event) return <div className="app-loading" aria-busy="true" />;

  const when = describeWhen(event.startsOn, event.endsOn);
  const counts = event.counts;
  const total = counts?.sponsors ?? 0;
  const percent = total > 0 ? Math.round(((counts?.approved ?? 0) / total) * 100) : 0;
  /* Red only when there is something to act on. A past deadline on a complete
     event is history, not an alarm. */
  const overdue =
    typeof event.deadline?.daysUntil === 'number' &&
    event.deadline.daysUntil <= 7 &&
    event.status === 'active';

  return (
    <>
      <Link className="back" to={`/c/${clientId}/events`}>
        <svg className="icon icon-sm" viewBox="0 0 24 24" aria-hidden="true">
          <path d="m15 18-6-6 6-6" />
        </svg>
        All Events</Link>

      <div className="phead">
        <div>
          <h1 className="title">{event.name}</h1>
          <div className="emeta">
            <span className={`badge ${STATUS_BADGE[event.status]}`}>
              <span className="d" />
              {event.status[0]!.toUpperCase() + event.status.slice(1)}
            </span>
            {when && <span>{when}</span>}
            {event.deadline?.display && (
              <span>
                Logo deadline: {event.deadline.display}
                {/* HOW SOON, which the header did not say. The date alone makes
                    a reader do the arithmetic, and the whole reason to open an
                    event is to find out whether it needs attention today. */}
                {typeof event.deadline.daysUntil === 'number' && (
                  <>
                    {' · '}
                    <b className={overdue ? 'hero-urgent' : undefined}>
                      {describeDays(event.deadline.daysUntil)}
                    </b>
                  </>
                )}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="tabs">
        <button
          type="button"
          className={`tab${tab === 'sponsors' ? ' active' : ''}`}
          onClick={() => setParams({}, { replace: true })}
        >
          Sponsors
        </button>
        <button
          type="button"
          className={`tab${tab === 'setup' ? ' active' : ''}`}
          onClick={() => setParams({ tab: 'setup' }, { replace: true })}
        >
          Event Setup
        </button>
      </div>

      {tab === 'sponsors' ? (
        <>
          {/* WHERE THIS EVENT STANDS, before the table of who. The numbers were
              already in the payload and nothing rendered them, so the page
              opened on a list of rows with no sense of whether the event was
              nearly done or barely started.

              On the Sponsors tab only. These describe the table underneath
              them; above Event Setup they would be decoration next to a form
              that cannot change them. */}
          {total > 0 && (
            <div className="stats">
              <div className="stat">
                <div className="label"><T k="stat.sponsors" d="Sponsors" /></div>
                <div className="n">{total}</div>
                <div className="m"><T k="eventDetail.stat.sponsorsSub" d="On this event" /></div>
              </div>
              <div className="stat">
                <div className="label"><T k="stat.approved" d="Approved" /></div>
                <div className="n">{counts?.approved ?? 0}</div>
                <div className="m">{percent}% complete</div>
              </div>
              <div className="stat">
                <div className="label"><T k="stat.pendingReview" d="Pending review" /></div>
                <div className="n">{counts?.pendingReview ?? 0}</div>
                <div className="m"><T k="stat.pendingReviewSub" d="Awaiting a decision" /></div>
              </div>
              <div className="stat">
                <div className="label"><T k="stat.outstanding" d="Outstanding" /></div>
                <div className="n">{counts?.outstanding ?? 0}</div>
                <div className="m"><T k="stat.outstandingSub" d="Awaiting upload" /></div>
              </div>
            </div>
          )}
          <Sponsors clientId={clientId} eventId={eventId} />
        </>
      ) : (
        <EventSetup clientId={clientId} event={event} onSaved={() => void load()} />
      )}

    </>
  );
}
